import React from "react";
import ScoreCard from "../ScoreCard";
import SkillBar from "../SkillBar";
import {
  FileText,
  Code,
  MessageSquare,
  ChevronRight,
  ChevronDown,
  PanelRight,
  FileCheck,
} from "lucide-react";
import { useSelector } from "react-redux";
import {
  getFeedbackForm,
  getInterviewResult,
  getProfileForm,
  getPsychometricSkills,
} from "../../../../redux/reducer/incbotFeedbackReducer";
import { interviewPayloadHasWritten } from "../writtenQuestionUtils";
import { getStatusV, normalizePsychometricScore } from "../../../../utils/helper";
import { QUESTION_TYPES, MCQ_SECTION_NAMES } from "../../../../constants";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../../ui/collapsible";
import PercentileRank from "../../../../assets/percentile-rank.svg";

function pctOf(num: number, den: number): number {
  if (!den || den <= 0) return 0;
  return Number(Math.min(100, (num / den) * 100).toFixed(1));
}

/** Score cells (e.g. overall obtained/total); avoids float noise when sections mix integers and decimals. */
function formatSectionScoreDisplay(value: number): string {
  if (!Number.isFinite(value)) return "0";
  return String(Number(value.toFixed(1)));
}

/** Prefers the conversational follow-up's rating (reflects the full exchange) over the base question rating. */
function getEffectiveRating(question: any): number {
  return Number(question?.conversationRatingAndDuration?.rating ?? question?.rating) || 0;
}

function ProgressBarCell({
  pct,
  barClassName = "bg-[linear-gradient(135deg,_#1375FF_0%,_#133F7D_100%)]",
}: {
  pct: number;
  barClassName?: string;
}) {
  return (
    <div className="flex min-w-0 items-center gap-2 sm:gap-3">
      <div className="h-1.5 sm:h-2 min-w-[48px] sm:min-w-[64px] flex-1 overflow-hidden rounded-full bg-gray-200">
        <div className={`h-1.5 sm:h-2 rounded-full ${barClassName}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="w-9 sm:w-11 shrink-0 text-right text-[10px] sm:text-xs tabular-nums text-gray-500">
        {pct}%
      </span>
    </div>
  );
}

/** Progress column: bar + % share one flexible width. */
function ProgressColumnWithTrailing({
  pct,
  barClassName,
}: {
  pct: number;
  trailing?: React.ReactNode;
  barClassName?: string;
}) {
  return (
    <div className="min-w-0 w-full">
      <ProgressBarCell pct={pct} barClassName={barClassName} />
    </div>
  );
}

function SubMetricPill({ label, pct }: { label: string; pct: number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-3 py-2.5 shadow-sm">
      <div className="mb-2 flex items-center justify-between gap-2">
        <span className="text-sm font-medium text-gray-900">{label}</span>
        <span className="text-xs tabular-nums text-gray-500">{pct}%</span>
      </div>
      <div className="h-1.5 w-full overflow-hidden rounded-full bg-gray-200">
        <div className="h-full rounded-full bg-[#8B92A0]" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

const OverviewView: React.FC = () => {
  const psychometricSkills = useSelector(getPsychometricSkills);
  const feedbackForm = useSelector(getFeedbackForm);
  const profileForm = useSelector(getProfileForm);
  const isPsychometricAssessment = profileForm?.interviewType === "psychometric_assessment";
  const interview = useSelector(getInterviewResult);

  const customQuestions = (feedbackForm?.questions?.data || [])?.filter(
    (question: any) =>
      question.questionType === QUESTION_TYPES.CUSTOM ||
      question.questionType === QUESTION_TYPES.AI_GENERATED
  );
  const [showMCQDetails, setShowMCQDetails] = React.useState(false);
  const [showPositionDetails, setShowPositionDetails] = React.useState(false);
  const isSkillBased = feedbackForm?.isSkillBased || false;
  const codingAndAptitude = feedbackForm?.codingAndAptitude || false;
  const skillQuestions = React.useMemo(() => {
    if (!isSkillBased) return {};

    return customQuestions.reduce((acc: any, question: any) => {
      if (question.skill) {
        if (!acc[question.skill]) {
          acc[question.skill] = [];
        }
        acc[question.skill].push(question);
      }
      return acc;
    }, {});
  }, [customQuestions, isSkillBased]);
  const getPsychometricLabel = (value: number) => {
    const { title } = getStatusV(value);
    return title === "Above Average" ? "Abv. Average" : title;
  };
  const psychometricSkillsScores = feedbackForm?.psychometricSkillsScores ?? [];
  const psychometricSkillWiseResults = feedbackForm?.psychometricSkillWise?.skill_results ?? [];
  const psychometricQuestionsFeedback = feedbackForm?.psychometricQuestionsFeedback ?? [];
  const psychometricSkillsWithFallback = React.useMemo(() => {
    const normalizedSkillWise = psychometricSkillWiseResults
      .map((row: { skill?: string; score?: number }) => ({
        title: String(row?.skill ?? "").trim(),
        value: Number(row?.score) || 0,
      }))
      .filter((row: { title: string }) => row.title);

    const scoreBySkill = new Map<string, number>();
    normalizedSkillWise.forEach((row: { title: string; value: number }) => {
      scoreBySkill.set(row.title.toLowerCase(), row.value);
    });

    const fallbackSkillsFromQuestions = psychometricQuestionsFeedback
      .map((question: { skill?: string }) => String(question?.skill ?? "").trim())
      .filter(Boolean)
      .filter((skill: string, index: number, arr: string[]) => arr.indexOf(skill) === index)
      .filter((skill: string) => !scoreBySkill.has(skill.toLowerCase()))
      .map((skill: string) => ({ title: skill, value: 0 }));

    const merged = [...normalizedSkillWise, ...fallbackSkillsFromQuestions];
    return merged.length > 0 ? merged : psychometricSkillsScores;
  }, [psychometricQuestionsFeedback, psychometricSkillsScores, psychometricSkillWiseResults]);
  const specificMarks = feedbackForm?.positionStats?.obtainedMarks || 0;
  const specificMarksTotal = feedbackForm?.positionStats?.totalMarks || 0;
  const codingMarks = feedbackForm?.codingStats?.obtainedMarks || 0;
  const codingMarksTotal = feedbackForm?.codingStats?.totalMarks || 0;
  const aptitudeMarks = feedbackForm?.aptitudeStats?.obtainedMarks || 0;
  const aptitudeMarksTotal = feedbackForm?.aptitudeStats?.totalMarks || 0;
  const writtenSectionData = (feedbackForm?.questions?.data || []).filter((question: any) => {
    const qt = String(question?.questionType || "").toLowerCase();
    return qt === QUESTION_TYPES.WRITING;
  });
  const getPsychometricCardTheme = (value: number) => {
    if (value < 5) {
      return {
        wrapper: "bg-rose-50/70 border-rose-200/80",
        score: "text-rose-700",
      };
    }
    if (value < 8) {
      return {
        wrapper: "bg-sky-50/70 border-sky-200/80",
        score: "text-sky-700",
      };
    }
    return {
      wrapper: "bg-emerald-50/70 border-emerald-200/80",
      score: "text-emerald-700",
    };
  };
  // Show Written row when the interview template lists written (`question.questions` / `questions`) or
  // feedback already has written rows. Scores use feedback written rows only when present.
  const hasWrittenInInterviewDefinition = interviewPayloadHasWritten(interview);
  const hasWrittenSection = hasWrittenInInterviewDefinition || writtenSectionData.length > 0;

  // Written questions use rating 0–10 each; section score is shown as obtained/1 (sum of ratings / (10 × n)).
  // Compute Overall Score from the same denominators so the footer matches what users see above.
  const writtenTotalMarks = hasWrittenSection ? writtenSectionData?.length : 0;
  const writtenScoredMarks = (() => {
    if (!hasWrittenSection) return 0;
    if (writtenSectionData.length === 0) return 0;
    const sum = writtenSectionData.reduce(
      (acc: number, q: any) => acc + (Number(q.rating) || 0),
      0
    );
    return sum / 10;
  })();

  const computedTotalMarks =
    (specificMarksTotal || 0) +
    (codingMarksTotal || 0) +
    (aptitudeMarksTotal || 0) +
    writtenTotalMarks;
  const computedScoredMarks =
    (specificMarks || 0) + (codingMarks || 0) + (aptitudeMarks || 0) + writtenScoredMarks;

  const totalMarks =
    computedTotalMarks > 0 ? computedTotalMarks : feedbackForm?.score?.totalMarks || 0;
  const scoredMarks =
    computedScoredMarks > 0 ? computedScoredMarks : feedbackForm?.score?.scoredMarks || 0;

  const totalStudents = feedbackForm?.totalParticipants || 0;
  const percentage =
    totalMarks > 0 ? (scoredMarks / totalMarks) * 100 : feedbackForm?.score?.percentage || 0;
  const psychometricOverallScoreRaw = Number(feedbackForm?.psychometricOverallScore);
  const hasPsychometricOverallScore =
    isPsychometricAssessment &&
    feedbackForm?.psychometricOverallScore != null &&
    Number.isFinite(psychometricOverallScoreRaw);
  const psychometricPercentileRaw = Number(feedbackForm?.psychometricPercentile);
  const hasPsychometricPercentile =
    isPsychometricAssessment &&
    feedbackForm?.psychometricPercentile != null &&
    Number.isFinite(psychometricPercentileRaw);
  const percentile = hasPsychometricPercentile
    ? psychometricPercentileRaw
    : feedbackForm?.finalPercentile || 0;
  const formatPercentage = (value: number) => (value % 1 !== 0 ? value.toFixed(1) : String(value));
  const percentileTopShare =
    percentile > 0 ? Number(Math.max(0, 100 - percentile).toFixed(1)) : null;
  /** Employer setting `percentile_rank`; absent settings keep the card visible. */
  const showPercentileRank = feedbackForm?.showPercentileRank !== false;
  const psychometricOverallPercentage =
    normalizePsychometricScore(feedbackForm?.psychometricOverallScore) ?? 0;
  const overallScoreValue = hasPsychometricOverallScore
    ? `${formatPercentage(psychometricOverallPercentage)}%`
    : percentage % 1 !== 0
      ? percentage.toFixed(1) + "%"
      : percentage + "%";
  const overallScoreProgress = hasPsychometricOverallScore
    ? Math.min(100, Math.max(0, psychometricOverallPercentage))
    : percentage;
  const overallScoreRightSideValue = hasPsychometricOverallScore
    ? `${formatPercentage(psychometricOverallPercentage)}% Score`
    : percentage % 1 !== 0
      ? percentage.toFixed(1) + "%"
      : percentage + "%" + " Score";
  const overallScoreSubtitle = "Total percentage";

  /** API uses `-1` when coding is disabled; otherwise an object with marks. */
  const codingStatsRaw = feedbackForm?.codingStats;
  const hasCodingStatsSection =
    codingStatsRaw != null && typeof codingStatsRaw === "object" && !Array.isArray(codingStatsRaw);

  const positionSkillBreakdown = React.useMemo(() => {
    if (!isSkillBased)
      return [] as {
        name: string;
        obtained: number;
        total: number;
        pct: number;
      }[];
    return Object.entries(skillQuestions).map(([name, qs]) => {
      const questions = qs as any[];
      const obtained = questions.reduce((s: number, q: any) => s + getEffectiveRating(q), 0);
      const total = questions.length * 10;
      return {
        name,
        obtained,
        total,
        pct: pctOf(obtained, total),
      };
    });
  }, [skillQuestions, isSkillBased]);

  const mcqTypeBreakdown = React.useMemo((): {
    label: string;
    obtained: number;
    total: number;
    pct: number;
  }[] => {
    const sections = feedbackForm?.aptitudeSectionData?.sectionsData ?? [];
    return sections.map((section: any) => {
      const responses = section.responses ?? [];
      const obtained = Number(section.totalMarks) || 0;
      const total = responses.length > 0 ? responses.length : obtained > 0 ? obtained : 1;
      const labelBase = MCQ_SECTION_NAMES[section.questionType] ?? section.questionType ?? "MCQ";
      const label = section.difficultyLevel
        ? `${labelBase} · ${section.difficultyLevel}`
        : labelBase;
      return {
        label,
        obtained,
        total,
        pct: pctOf(obtained, total),
      };
    });
  }, [feedbackForm?.aptitudeSectionData?.sectionsData]);

  const skills = React.useMemo(() => {
    return Object.entries(skillQuestions).map(([skillName, questions]: any) => {
      const totalRating = questions.reduce((sum: any, q: any) => sum + getEffectiveRating(q), 0);
      const avgRating = questions.length > 0 ? totalRating / questions.length : 0;
      const pct = (avgRating / 10) * 100;

      return {
        name: skillName,
        percentage: Number(pct.toFixed(1)),
      };
    });
  }, [skillQuestions]);

  const hasSkillWiseOverallScore = isSkillBased && skills.length > 0;

  const suspiciousValue = feedbackForm?.overallFeedback?.[2]?.value || 0;

  const getSuspiciousLevel = (value: number) => {
    if (value > 7) return { level: "HIGH", label: "High", type: "alert", color: "red" };
    if (value > 4)
      return {
        level: "MEDIUM",
        label: "Medium",
        type: "percentile",
        color: "yellow",
      };
    return { level: "LOW", label: "Low", type: "score", color: "blue" };
  };

  const { label, type, color } = getSuspiciousLevel(suspiciousValue);

  const hasSectionWiseScores =
    specificMarksTotal > 0 ||
    codingMarksTotal > 0 ||
    aptitudeMarksTotal > 0 ||
    hasWrittenSection ||
    totalMarks > 0;

  const positionPct = pctOf(specificMarks, specificMarksTotal);
  const codingPct = pctOf(codingMarks, codingMarksTotal);
  const mcqPct = pctOf(aptitudeMarks, aptitudeMarksTotal);
  const writtenPct = hasWrittenSection ? pctOf(writtenScoredMarks, writtenTotalMarks) : 0;
  const overallPct = pctOf(scoredMarks, totalMarks);
  const parseRemarks = (data: string) => {
    if (!data) return {} as Record<string, string[]>;

    const sections: Record<string, string[]> = {};
    let currentSection = "";

    data.split("\n").forEach((line) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) return;

      if (trimmedLine.endsWith(":")) {
        currentSection = trimmedLine.slice(0, -1);
        sections[currentSection] = [];
      } else if (trimmedLine.startsWith("-") && currentSection) {
        sections[currentSection].push(trimmedLine.substring(1).trim());
      }
    });

    return sections;
  };

  const remarksData = parseRemarks(feedbackForm?.finalRemarks?.data || "");
  const psychometricConclusion = String(
    feedbackForm?.psychometricOverallReport?.conclusion || ""
  ).trim();
  const additionalConclusionLine =
    profileForm?.status === "Recommended"
      ? "Based on the interview, the candidate is highly recommended for the position and is expected to be a valuable addition to the team."
      : profileForm?.status === "Cautiously Recommended"
        ? "The candidate has shown some strengths but also has areas that need improvement. They are cautiously recommended for further consideration."
        : "Based on the evaluation, the candidate does not meet the necessary requirements for the position and is not recommended for further consideration.";
  const conclusionItems = isPsychometricAssessment
    ? [psychometricConclusion].filter(Boolean)
    : [...(remarksData["Conclusion"] || []), additionalConclusionLine].filter(Boolean);

  const sectionWiseScoresSection =
    hasSectionWiseScores && !isPsychometricAssessment ? (
      <div key="scores" className="bg-white rounded-lg border border-gray-200">
        <h2 className="mb-4 flex items-center gap-2 text-md font-semibold text-gray-900 md:mb-5 md:text-lg p-4 border-b border-[#F3F4F6]">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-600">
            <PanelRight className="h-4 w-4" aria-hidden />
          </span>
          Section-wise Scores
        </h2>
        <div className="px-2.5 sm:px-4 pb-3 sm:pb-4">
          <div className="overflow-hidden rounded-lg border border-gray-200">
            <div>
              {/* Table header */}
              <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(52px,0.55fr)_minmax(88px,1fr)_20px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(80px,0.6fr)_minmax(150px,1fr)_32px] sm:gap-3 border-b border-[#F1F5F999] bg-slate-50/90 px-3 sm:px-4 py-2.5 sm:py-3 text-[11px] sm:text-xs font-medium tracking-wide text-gray-500">
                <div className="min-w-0 text-left text-[#64748B] font-bold text-[12px] sm:text-[13px]">
                  Section
                </div>
                <div className="flex justify-center text-center text-[#64748B] font-bold text-[12px] sm:text-[13px]">
                  Score
                </div>
                <div className="col-span-2 text-center text-[#64748B] font-bold text-[12px] sm:text-[13px]">
                  Progress
                </div>
              </div>

              <div className="divide-y divide-gray-200 bg-white">
                {/* Position Specific — accordion when skill breakdown exists */}
                {specificMarksTotal > 0 &&
                  (positionSkillBreakdown.length > 0 ? (
                    <Collapsible defaultOpen>
                      <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(52px,0.55fr)_minmax(88px,1fr)_20px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(80px,0.6fr)_minmax(150px,1fr)_32px] sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 items-center">
                        <div className="font-semibold text-gray-900 text-sm">Position Specific</div>
                        <div className="flex w-full items-center justify-center text-center text-xs sm:text-sm font-normal tabular-nums text-gray-900">
                          {specificMarks}/{specificMarksTotal}
                        </div>
                        <ProgressColumnWithTrailing pct={positionPct} />
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center sm:h-8 sm:w-8">
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-transparent text-gray-500 outline-none hover:bg-transparent active:bg-transparent focus:bg-transparent focus-visible:ring-2 focus-visible:ring-blue-500/40 sm:h-8 sm:w-8 data-[state=open]:[&_svg]:rotate-90"
                              aria-label="Toggle Position Specific details"
                              onClick={() => setShowPositionDetails(!showPositionDetails)}
                            >
                              {!showPositionDetails ? (
                                <ChevronDown
                                  className="h-3 w-3 transition-transform sm:h-4 sm:w-4"
                                  aria-hidden
                                />
                              ) : (
                                <ChevronRight
                                  className="h-3 w-3 transition-transform sm:h-4 sm:w-4"
                                  aria-hidden
                                />
                              )}
                            </button>
                          </CollapsibleTrigger>
                        </div>
                      </div>
                      <CollapsibleContent>
                        <div className="border-t border-gray-100 bg-slate-50/60 px-4 py-4">
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                            {positionSkillBreakdown.map((row) => (
                              <SubMetricPill key={row.name} label={row.name} pct={row.pct} />
                            ))}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(52px,0.55fr)_minmax(88px,1fr)_20px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(80px,0.6fr)_minmax(150px,1fr)_32px] sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 items-center">
                      <div className="font-semibold text-gray-900 text-sm">Position Specific</div>
                      <div className="flex w-full items-center justify-center text-center text-xs sm:text-sm font-normal tabular-nums text-gray-900">
                        {specificMarks}/{specificMarksTotal}
                      </div>
                      <ProgressColumnWithTrailing pct={positionPct} />
                    </div>
                  ))}

                {/* Coding — plain row */}
                {codingMarksTotal > 0 && (
                  <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(52px,0.55fr)_minmax(88px,1fr)_20px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(80px,0.6fr)_minmax(150px,1fr)_32px] sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 items-center">
                    <div className="font-semibold text-gray-900 text-sm">Coding</div>
                    <div className="flex w-full items-center justify-center text-center text-xs sm:text-sm font-normal tabular-nums text-gray-900">
                      {codingMarks}/{codingMarksTotal}
                    </div>
                    <ProgressColumnWithTrailing pct={codingPct} />
                  </div>
                )}

                {/* MCQ — accordion when aptitude sections exist */}
                {aptitudeMarksTotal > 0 &&
                  (mcqTypeBreakdown.length > 0 ? (
                    <Collapsible defaultOpen={false}>
                      <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(52px,0.55fr)_minmax(88px,1fr)_20px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(80px,0.6fr)_minmax(150px,1fr)_32px] sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 items-center">
                        <div className="font-semibold text-gray-900 text-sm">MCQ</div>
                        <div className="flex w-full items-center justify-center text-center text-xs sm:text-sm font-normal tabular-nums text-gray-900">
                          {aptitudeMarks}/{aptitudeMarksTotal}
                        </div>
                        <ProgressColumnWithTrailing pct={mcqPct} />
                        <div className="flex h-5 w-5 shrink-0 items-center justify-center sm:h-8 sm:w-8">
                          <CollapsibleTrigger asChild>
                            <button
                              type="button"
                              className="flex h-5 w-5 shrink-0 items-center justify-center rounded bg-transparent text-gray-500 outline-none hover:bg-transparent active:bg-transparent focus:bg-transparent focus-visible:ring-2 focus-visible:ring-blue-500/40 sm:h-8 sm:w-8 data-[state=open]:[&_svg]:rotate-90"
                              aria-label="Toggle MCQ details"
                              onClick={() => setShowMCQDetails(!showMCQDetails)}
                            >
                              {showMCQDetails ? (
                                <ChevronDown
                                  className="h-3 w-3 transition-transform sm:h-4 sm:w-4"
                                  aria-hidden
                                />
                              ) : (
                                <ChevronRight
                                  className="h-3 w-3 transition-transform sm:h-4 sm:w-4"
                                  aria-hidden
                                />
                              )}
                            </button>
                          </CollapsibleTrigger>
                        </div>
                      </div>
                      <CollapsibleContent>
                        <div className="border-t border-gray-100 bg-slate-50/60 px-4 py-4">
                          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
                            {mcqTypeBreakdown.map((row, idx) => (
                              <SubMetricPill
                                key={`${row.label}-${idx}`}
                                label={row.label}
                                pct={row.pct}
                              />
                            ))}
                          </div>
                        </div>
                      </CollapsibleContent>
                    </Collapsible>
                  ) : (
                    <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(52px,0.55fr)_minmax(88px,1fr)_20px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(80px,0.6fr)_minmax(150px,1fr)_32px] sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 items-center">
                      <div className="font-semibold text-gray-900 text-sm">MCQ</div>
                      <div className="flex w-full items-center justify-center text-center text-xs sm:text-sm font-normal tabular-nums text-gray-900">
                        {aptitudeMarks}/{aptitudeMarksTotal}
                      </div>
                      <ProgressColumnWithTrailing pct={mcqPct} />
                    </div>
                  ))}

                {/* Written — plain row */}
                {hasWrittenSection && (
                  <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(52px,0.55fr)_minmax(88px,1fr)_20px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(80px,0.6fr)_minmax(150px,1fr)_32px] sm:gap-3 px-3 sm:px-4 py-3 sm:py-3.5 items-center">
                    <div className="font-semibold text-gray-900 text-sm">Written</div>
                    <div className="flex w-full items-center justify-center text-center text-xs sm:text-sm font-normal tabular-nums text-gray-900">
                      {formatSectionScoreDisplay(writtenScoredMarks)}/
                      {formatSectionScoreDisplay(writtenTotalMarks)}
                    </div>
                    <ProgressColumnWithTrailing pct={writtenPct} />
                  </div>
                )}

                {/* Overall score — footer */}
                {totalMarks > 0 && (
                  <div className="grid grid-cols-[minmax(0,1.1fr)_minmax(52px,0.55fr)_minmax(88px,1fr)_20px] gap-1.5 sm:grid-cols-[minmax(0,1fr)_minmax(80px,0.6fr)_minmax(150px,1fr)_32px] sm:gap-3 bg-slate-50/80 px-3 sm:px-4 py-3 sm:py-3.5 items-center">
                    <div className="font-semibold text-gray-900 text-sm">Overall Score</div>
                    <div className="flex w-full items-center justify-center text-center text-xs sm:text-sm font-semibold tabular-nums text-gray-900">
                      {formatSectionScoreDisplay(scoredMarks)}/
                      {formatSectionScoreDisplay(totalMarks)}
                    </div>
                    <ProgressColumnWithTrailing pct={overallPct} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    ) : null;

  const communicationSection =
    !codingAndAptitude && !isPsychometricAssessment ? (
      <div key="communication" className="bg-white rounded-xl border border-gray-200">
        <h2 className="flex items-center text-lg font-semibold px-2 py-3 border-b border-[#F3F4F6]">
          <span className="bg-[#EEF2FF] py-2 px-2 mr-2 rounded-md ml-2 bg-emerald-50 text-emerald-600">
            <MessageSquare className="w-4 h-4 md:w-5 md:h-5 text-emerald-600" />
          </span>
          Communication Assessment
        </h2>
        <div
          className={`grid ${
            // softSkillsCount === 1 ? "grid-cols-1" : "grid-cols-2"
            hasSkillWiseOverallScore
              ? "grid-cols-1 sm:grid-cols-2 md:grid-cols-2 p-4"
              : "grid-cols-1 sm:grid-cols-2 md:grid-cols-4 p-4"
          } gap-4`}
        >
          {feedbackForm?.softSkillAssessment
            ?.filter((item: { title: string; value: number; status?: boolean }) => item.status)
            ?.map((item: { title: string; value: number }, key: number) => {
              const isConfidence = item.title === "Confidence";

              const description = isConfidence
                ? item.value > 8
                  ? "High"
                  : item.value > 6
                    ? "Good"
                    : item.value > 4
                      ? "Moderate"
                      : "Low"
                : getStatusV(item.value).title;

              const cardColor = isConfidence
                ? item.value > 8
                  ? "green"
                  : item.value > 4
                    ? "blue"
                    : "red"
                : (getStatusV(item.value).color as "blue" | "red" | "green" | "yellow");

              const cardTheme =
                cardColor === "green"
                  ? {
                      wrapper: "bg-emerald-50/70 border-emerald-200/80",
                      score: "text-emerald-700",
                    }
                  : cardColor === "red"
                    ? {
                        wrapper: "bg-rose-50/70 border-rose-200/80",
                        score: "text-rose-700",
                      }
                    : cardColor === "yellow"
                      ? {
                          wrapper: "bg-amber-50/70 border-amber-200/80",
                          score: "text-amber-700",
                        }
                      : {
                          wrapper: "bg-sky-50/70 border-sky-200/80",
                          score: "text-sky-700",
                        };

              return (
                <div key={key} className={`rounded-lg border px-3 py-3.5 ${cardTheme.wrapper}`}>
                  <div className="flex items-center justify-between gap-3">
                    <h3 className="text-[15px] font-semibold text-gray-800 leading-tight">
                      {item.title}
                    </h3>
                    <div className="text-right shrink-0">
                      <div
                        className={`text-3xl font-semibold leading-none tracking-tight ${cardTheme.score}`}
                      >
                        {item.value}
                        <span className="text-xl text-gray-500 font-medium">/10</span>
                      </div>
                      <p className="text-xs font-medium text-gray-600 mt-1">{description}</p>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>
      </div>
    ) : null;

  const resumeSection = feedbackForm?.resumeSummary ? (
    <div key="resume" className="bg-white rounded-lg border border-gray-200">
      <div className="flex items-center space-x-2">
        <h2 className="flex items-center text-lg font-semibold px-2 py-3 border-b border-[#F3F4F6]">
          <span className="bg-[#EEF2FF] py-2 px-2 mr-2 rounded-md ml-2">
            <FileText className="w-4 h-4 md:w-5 md:h-5 text-[#133F7D]" />
          </span>
          Resume Summary
        </h2>
      </div>
      <div className="space-y-1 md:space-y-3 p-4">
        {feedbackForm?.resumeSummary
          ?.split("\n")
          .filter((point: string) => point.trim())
          .map((point: any, index: any) => (
            <div key={index} className="flex items-start space-x-2">
              <div className="h-1 w-1 md:w-2 md:h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs md:text-sm text-gray-700">{point.replace(/^-\s*/, "")}</p>
            </div>
          ))}
      </div>
    </div>
  ) : null;

  const conclusionSection =
    conclusionItems.length > 0 ? (
      <div key="conclusion" className="bg-white rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2">
          <h2 className="flex items-center text-lg font-semibold px-2 py-3 border-b border-[#F3F4F6]">
            <span className="bg-[#EEF2FF] py-2 px-2 mr-2 rounded-md ml-2">
              <FileCheck className="w-4 h-4 md:w-5 md:h-5 text-[#133F7D]" />
            </span>
            Conclusion
          </h2>
        </div>
        <div className="space-y-1 md:space-y-3 p-4">
          {conclusionItems.map((item, index) => (
            <div key={index} className="flex items-start space-x-2">
              <div className="h-1 w-1 md:w-2 md:h-2 bg-blue-500 rounded-full mt-2 flex-shrink-0"></div>
              <p className="text-xs md:text-sm text-gray-700">{item}</p>
            </div>
          ))}
        </div>
      </div>
    ) : null;

  const skillsSection =
    isSkillBased && skills.length > 0 ? (
      <div key="skills" className="bg-white p-4 md:p-6 rounded-lg border border-gray-200">
        <div className="flex items-center space-x-2 mb-3 md:mb-4">
          <Code className="w-4 h-4 md:w-5 md:h-5 text-gray-600" />
          <h2 className="text-md md:text-lg font-semibold text-gray-900">
            Skill wise Overall Score
          </h2>
        </div>
        <div className={`grid ${skills.length > 4 ? "md:grid-cols-2" : "grid-cols-1"} gap-4 `}>
          {skills.map((skill, index) => (
            <SkillBar key={index} skill={skill.name} percentage={skill.percentage} />
          ))}
        </div>
      </div>
    ) : null;

  const summarySections = [resumeSection, conclusionSection].filter(Boolean);

  return (
    <div className="p-3 md:p-6">
      <div className="md:hidden print:flex w-100 p-1 print:p-2 rounded-md border-b-4 border-teal-500 bg-teal-50 text-teal-500 text-md print:text-xl flex justify-center font-semibold mb-3 md:mb-6">
        Overview
      </div>
      {/* Score Overview */}
      <div className="border bg-[#FFFFFF] rounded-lg mb-3">
        <h2 className="flex items-center text-lg font-semibold px-2 py-3 border-b border-[#F3F4F6]">
          <span className="bg-[#EEF2FF] py-2 px-2 mr-2 rounded-md ml-2">
            <FileText className="w-4 h-4 md:w-5 md:h-5 text-[#133F7D]" />
          </span>
          Score Overview
        </h2>
        <div
          className={`grid grid-cols-1 ${
            showPercentileRank ? "md:grid-cols-3" : "md:grid-cols-2"
          } gap-3 md:gap-3 p-4`}
        >
          <ScoreCard
            title="Overall Score"
            value={overallScoreValue}
            subtitle={overallScoreSubtitle}
            type="score"
            progress={overallScoreProgress}
            color="teal"
            rightSideValue={overallScoreRightSideValue}
          />
          {showPercentileRank && (
            <ScoreCard
              title="Percentile Rank"
              value={String(percentile)}
              subtitle={`Out of ${totalStudents} test takers`}
              type="percentile"
              progress={percentile}
              color="lightblue"
              rightSideValue={
                isPsychometricAssessment
                  ? percentileTopShare != null
                    ? `Top ${formatPercentage(percentileTopShare)}%`
                    : "-"
                  : `Top 5%`
              }
            />
          )}
          <ScoreCard
            title="Suspicious Activity"
            value={label}
            subtitle={`Suspicious activity detected`}
            type={type as "resume" | "score" | "percentile" | "alert"}
            progress={suspiciousValue * 10}
            color={color as "blue" | "green" | "red" | "yellow" | "teal"}
            rightSideValue={
              label === "High" ? "High Risk" : label === "Medium" ? "Medium Risk" : "Low Risk"
            }
          />
        </div>
      </div>

      {/* Psychometric Skills Scores */}
      {isPsychometricAssessment && psychometricSkillsWithFallback.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 mb-3 md:mb-4">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900 px-2 py-3 border-b border-[#F3F4F6]">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-emerald-50 text-emerald-600 border border-emerald-200 ml-2">
              <MessageSquare className="h-4 w-4 text-emerald-600" aria-hidden />
            </span>
            Psychometric Skills Scores
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 p-4 md:p-6">
            {(psychometricSkills?.length > 0
              ? psychometricSkills
              : psychometricSkillsWithFallback
            ).map((skill: any) => {
              const skillName =
                typeof skill === "string" ? skill : String(skill?.skill ?? skill?.title ?? "");
              const matched = psychometricSkillsWithFallback.find(
                (f: { title: string; value: number }) =>
                  f.title.toLowerCase() === skillName.toLowerCase()
              );
              const item: { title: string; value: number } = matched ?? {
                title: skillName,
                value: 0,
              };
              const displayValue = Number.isFinite(item.value) ? Math.round(item.value) : 0;
              const cardTheme = getPsychometricCardTheme(displayValue);
              const description = getPsychometricLabel(displayValue);
              return (
                <div
                  key={item.title}
                  className={`rounded-lg border px-3 py-3.5 ${cardTheme.wrapper}`}
                >
                  <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1 items-center">
                    <h3 className="text-[12px] md:text-[15px] font-semibold text-gray-800 leading-snug min-w-0 break-words">
                      {item.title}
                    </h3>
                    <div className="text-center shrink-0 justify-self-end min-w-[4.25rem] sm:min-w-[4.5rem]">
                      <div
                        className={`text-xl md:text-2xl font-semibold leading-none tracking-tight tabular-nums ${cardTheme.score}`}
                      >
                        {String(displayValue).padStart(2, "0")}
                        <span className="text-base md:text-xl text-gray-500 font-medium">/10</span>
                      </div>
                      <p className="text-[10px] sm:text-xs text-center font-medium text-gray-600 mt-1 whitespace-normal break-words">
                        {description}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Section-wise Scores + Communication */}
      {(sectionWiseScoresSection || communicationSection) && (
        <div className="space-y-3 md:space-y-3">
          {sectionWiseScoresSection}

          {communicationSection &&
            (hasSkillWiseOverallScore ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-3">
                {communicationSection}
                {skillsSection}
              </div>
            ) : (
              communicationSection
            ))}
        </div>
      )}

      {isPsychometricAssessment && skillsSection ? (
        <div className="mt-3 md:mt-3">{skillsSection}</div>
      ) : null}

      {/* Resume Summary + Conclusion */}
      {summarySections.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 md:gap-3 mt-3">{summarySections}</div>
      )}
    </div>
  );
};

export default OverviewView;
